import { isValid, parse, parseISO } from 'date-fns';

/**
 * The observability search backend forwards a comparison value to ClickHouse as a string literal,
 * and ClickHouse only converts `YYYY-MM-DD[ T]HH:mm:ss[.fraction]`, `YYYY-MM-DD`, or a unix number
 * to a DateTime column. A 12-hour clock, a `Z`, or a `+01:00` offset all fail the query with
 * `Cannot convert string ... to type DateTime64(9)`. Timestamps are rendered in the browser's zone
 * on a 12-hour clock, so a value copied out of a table has to be rewritten before it reaches the
 * query — both the format and the offset.
 */

/** Exactly the forms ClickHouse converts on its own. */
const CLICKHOUSE_LITERAL_RE = /^(\d+|\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}:\d{2}(\.\d+)?)?)$/;

/**
 * Human-readable shapes read as local wall-clock time, in match order. The first two are what the
 * UI renders (`TimeContent`, `TIMESTAMP_FORMAT`); `HH:mm` is included because ClickHouse rejects a
 * minute-precision literal outright, so it can only have come from a person typing.
 */
const LOCAL_INPUT_FORMATS = ['yyyy-MM-dd hh:mm:ss a', 'yyyy-MM-dd hh:mm a', 'yyyy-MM-dd HH:mm'];

/** Only fills in milliseconds; every accepted format supplies the rest. */
const REFERENCE_DATE = new Date(0);

/** The canonical top-level field the backend stores as a DateTime column. */
export function isTimestampFieldName(field: string): boolean {
  return field === 'timestamp';
}

function toClickHouseLiteral(date: Date): string {
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * Rewrites a timestamp a person can read into the UTC literal ClickHouse parses. Values already in
 * a ClickHouse-native form are returned untouched, which is what keeps this idempotent: the output
 * is itself native, so re-serializing a committed query (a reload, a permalink, an edit to another
 * filter) can never shift the time by the viewer's offset. Anything unparseable is passed through
 * so the backend reports it, rather than this guessing at a time the user did not ask for.
 */
export function normalizeTimestampValue(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '' || CLICKHOUSE_LITERAL_RE.test(trimmed)) {
    return trimmed;
  }

  for (const format of LOCAL_INPUT_FORMATS) {
    const local = parse(trimmed, format, REFERENCE_DATE);
    if (isValid(local)) {
      return toClickHouseLiteral(local);
    }
  }

  // ISO-8601 with an explicit `Z` or offset: the instant is unambiguous, only the spelling is wrong.
  const absolute = parseISO(trimmed);
  if (isValid(absolute)) {
    return toClickHouseLiteral(absolute);
  }

  return value;
}
